// Contenu du coaching (prestation humaine de Julien Dauzats). Aucun prix ici : le serveur est la seule
// autorité (cf. CLAUDE.md). Aucune adresse personnelle non plus — `COACHING_INBOX_EMAIL` a été
// retirée le 19/10/2026 : elle n'était lue que par son propre test, et une adresse en clair dans un
// dépôt est une donnée personnelle gratuite pour quiconque le lit.

// Compte Cal.com qui porte les créneaux de Julien.
export const CAL_USERNAME = 'hormeia';

// Les identifiants restent `session_30` / `vip_60` : l'edge function `coaching-payment` fige
// les MONTANTS côté serveur à partir de ces clés. Les renommer imposerait un déploiement
// coordonné app + serveur, et une erreur de synchro ferait payer le mauvais prix.
export type CoachingFormuleId = 'session_30' | 'vip_60';

export interface CoachingFormule {
  id: CoachingFormuleId;
  title: string;
  subtitle: string; // accroche courte sous le titre
  // ⚠️ AUCUN PRIX ICI, ET C'EST UNE RÈGLE, PAS UN OUBLI. Le montant vient du serveur
  // (`coaching-payment`, route de lecture) ; l'écrire ici recréerait la seconde vérité que ce lot
  // a supprimée, et un écart entre prix annoncé et prix débité est une pratique trompeuse.
  // Un test interdit tout littéral de prix dans ce fichier.
  duration: string;
  points: string[];
  /** Slug Cal.com du type de rendez-vous (après `cal.com/hormeia/`). */
  calSlug: string;
  highlight?: boolean; // formule mise en avant (contour dégradé + CTA plein)
  badge?: string;
}

export const COACHING_FORMULES: CoachingFormule[] = [
  {
    id: 'vip_60',
    title: 'Session Premium 45’',
    subtitle: 'Le coaching complet pour des résultats concrets.',
    duration: '45 min',
    calSlug: '15min', // slug historique Cal.com : l'événement dure bien 45 min
    highlight: true,
    badge: 'Le plus réservé',
    points: [
      'Accompagnement complet en direct avec Julien Dauzats',
      'Plan d’action personnalisé',
      'Suivi et conseils exclusifs',
    ],
  },
  {
    id: 'session_30',
    title: 'Session Focus 30’',
    subtitle: 'Un point ciblé pour avancer rapidement.',
    duration: '30 min',
    calSlug: '30min',
    badge: 'Idéal pour commencer',
    points: [
      'Préparation d’entretien ciblée',
      'Conseils ressources & LinkedIn',
      'Point stratégie de candidature',
    ],
  },
];

export function findFormule(id: CoachingFormuleId): CoachingFormule | undefined {
  return COACHING_FORMULES.find((f) => f.id === id);
}

/**
 * URL de réservation Cal.com pour une formule, pré-remplie avec l'identité de l'utilisateur.
 * Les paramètres `name` / `email` sont ceux acceptés par le Booker de Cal.com.
 */
export function calBookingUrl(f: CoachingFormule, prefill?: { name?: string; email?: string }): string {
  const params = new URLSearchParams();
  if (prefill?.name) params.set('name', prefill.name);
  if (prefill?.email) params.set('email', prefill.email);
  params.set('theme', 'light');
  return `https://cal.com/${CAL_USERNAME}/${f.calSlug}?${params.toString()}`;
}

// Un segment de crédibilité ; `bold` met le début de phrase en gras.
export interface CoachCredSegment {
  text: string;
  bold?: boolean;
}

/** Une des trois colonnes de chiffres clés sous l'identité de l'expert. */
export interface CoachStat {
  icon: 'badge' | 'community' | 'mic';
  value: string; // ligne forte
  label: string; // ligne secondaire
}

export interface CoachExpert {
  name: string;
  initials: string;
  roleLead: string; // « Expert RH & recrutement » (gris)
  roleBrand: string; // « Fondateur d'Horméia » (dégradé charte)
  stats: CoachStat[];
  credentials: CoachCredSegment[][]; // conservé : utilisé ailleurs (accueil, paywall)
}

export const JULIEN: CoachExpert = {
  name: 'Julien Dauzats',
  initials: 'JD',
  roleLead: 'Expert RH & recrutement',
  roleBrand: 'Fondateur d’Horméia',
  stats: [
    { icon: 'badge', value: '25 ans', label: 'd’expérience' },
    { icon: 'community', value: '125 000+', label: 'communautés' },
    { icon: 'mic', value: 'Interventions, podcasts', label: 'et accompagnement' },
  ],
  credentials: [
    [{ text: '25 ans d’expérience', bold: true }, { text: ' en recrutement, formation et insertion' }],
    [{ text: '125 000 membres', bold: true }, { text: ' sur la communauté LinkedIn AlternJob' }],
    [
      { text: 'Interventions, podcasts', bold: true },
      { text: ' et ' },
      { text: 'accompagnement', bold: true },
      { text: ' auprès d’entreprises et de jeunes talents' },
    ],
  ],
};
